import { Permission } from 'src/contstant/permissionAccessConfig';
import { useUserDetails } from 'src/providers/UserProvider';

/**
 * A component that conditionally renders its children based on user permissions.
 *
 * This component checks if the current user has the required permissions and only
 * renders the children if the permission check passes. If the user lacks the required
 * permissions, the component renders the `unauthorizedComponent` (or null if not provided).
 *
 * @example
 * // Require all specified permissions (default behavior)
 * <Authorized permissionsToCheck={[permissionAccessConfig.LOCATION.EDIT_LOCATION]}>
 *   <Button>Create Location</Button>
 * </Authorized>
 *
 * @example
 * // Require any one of the specified permissions
 * <Authorized
 *   permissionsToCheck={[
 *     permissionAccessConfig.USERS.EDIT_USER,
 *     permissionAccessConfig.PERMISSIONS.EDIT_PERMISSIONS
 *   ]}
 *   method="ANY"
 * >
 *   <Button>Edit</Button>
 * </Authorized>
 *
 * @example
 * // Render a custom component when unauthorized
 * <Authorized
 *   permissionsToCheck={[permissionAccessConfig.LOCATION.EDIT_LOCATION]}
 *   unauthorizedComponent={<span>You don't have permission to edit locations</span>}
 * >
 *   <Button>Create Location</Button>
 * </Authorized>
 *
 * @param props - The component props
 * @param props.permissionsToCheck - An array of Permission strings that the user must have
 * @param props.children - The React nodes to render if the permission check passes
 * @param props.method - The permission checking method:
 *   - 'ALL' (default): User must have all permissions in `permissionsToCheck`
 *   - 'ANY': User must have at least one permission in `permissionsToCheck`
 * @param props.unauthorizedComponent - Optional React node to render when the user is not authorized.
 *   If not provided, the component returns null (renders nothing). Defaults to null.
 *
 * @returns The children if authorized, otherwise the `unauthorizedComponent` (or null if not provided)
 */
export function Authorized({
  permissionsToCheck,
  children,
  method = 'ALL',
  unauthorizedComponent = null,
}: {
  permissionsToCheck: Permission[];
  children: React.ReactNode;
  method?: 'ALL' | 'ANY';
  unauthorizedComponent?: React.ReactNode | null;
}) {
  const { permissions } = useUserDetails();

  // Check if user has required permissions based on the method
  const isAuthorized =
    method === 'ALL'
      ? permissionsToCheck.every((permission) => permissions.has(permission))
      : permissionsToCheck.some((permission) => permissions.has(permission));

  if (!isAuthorized) {
    return <>{unauthorizedComponent}</>;
  }

  return <>{children}</>;
}
